<template>
  <div class="flex flex-nowrap items-center gap-2 mb-3 w-full">
    <!-- 带下拉和内联标签的搜索输入框 -->
    <div class="relative flex-1 h-10">

      <!-- 带内联标签和下拉的输入框 -->
      <div class="relative w-full h-full">
        <div class="inline-tags-container h-full">
          <!-- 搜索键下拉菜单 -->
          <div
            v-show="showSearchDropdown"
            class="absolute z-50 top-full left-0 w-48 bg-white shadow-lg rounded-md border border-gray-200 mt-1"
            @click.stop
            :style="{ marginTop: '0px' }"
            ref="searchDropdown"
          >
            <div
              v-for="key in searchKeys"
              :key="key.value"
              @click="selectSearchKey(key.value)"
              class="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
            >
              {{ key.label }}
            </div>
          </div>

          <!-- 内联标签 -->
          <div
            v-for="(criteria, index) in searchCriteria"
            :key="index"
            class="inline-tag"
            :class="{ 'editing': editingIndex === index }"
          >
            <template v-if="editingIndex !== index">
              <span class="font-medium">{{ getSearchKeyLabel(criteria.key) }}:</span>
              <span class="ml-1">{{ getDisplayValue(criteria.key, criteria.value) }}</span>
              <button
                @click.stop="editSearchCriteria(index)"
                class="ml-1 text-blue-500 hover:text-blue-700"
                title="编辑"
              >
                <i class="fas fa-edit text-xs"></i>
              </button>
              <button
                @click.stop="removeSearchCriteria(index)"
                class="ml-1"
                title="删除"
              >
                <i class="fas fa-times text-xs"></i>
              </button>
            </template>
            <template v-else>
              <template v-if="getKeyType(criteria.key) === 'text'">
                <input
                  type="text"
                  v-model="searchInputValue"
                  :placeholder="`编辑${getSearchKeyLabel(criteria.key)}...`"
                  class="inline-edit-input"
                  @keyup.enter="updateSearchCriteria(index)"
                  @keyup.escape="cancelEdit"
                  @blur="updateSearchCriteria(index)"
                  ref="editInput"
                />
              </template>
              <template v-else-if="getKeyType(criteria.key) === 'select'">
                <div class="relative">
                  <input
                    type="text"
                    :value="getEditDisplayValue(criteria.value)"
                    :placeholder="`选择${getSearchKeyLabel(criteria.key)}...`"
                    class="inline-edit-input"
                    @focus="showValueDropdown = true"
                    readonly
                    ref="editInput"
                  />
                  <div
                    v-show="showValueDropdown"
                    class="absolute z-50 top-full left-0 w-full bg-white shadow-lg rounded-md border border-gray-200 mt-1 max-h-60 overflow-y-auto"
                    @click.stop
                  >
                    <div
                      v-for="option in valueDropdownOptions"
                      :key="option.value"
                      @click="selectEditValue(option.value, index)"
                      class="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                    >
                      {{ option.label }}
                    </div>
                  </div>
                </div>
              </template>
              <template v-else-if="getKeyType(criteria.key) === 'multiselect'">
                <div class="relative">
                  <input
                    type="text"
                    :value="getEditDisplayValue(criteria.value)"
                    :placeholder="`选择${getSearchKeyLabel(criteria.key)}...`"
                    class="inline-edit-input"
                    @focus="showValueDropdown = true"
                    readonly
                    ref="editInput"
                  />
                  <div
                    v-show="showValueDropdown"
                    class="absolute z-50 top-full left-0 w-full bg-white shadow-lg rounded-md border border-gray-200 mt-1 max-h-60 overflow-y-auto"
                    @click.stop
                  >
                    <div
                      v-for="option in valueDropdownOptions"
                      :key="option.value"
                      @click="toggleEditMultiSelectValue(option.value, index)"
                      :class="[
                        'px-4 py-2 text-sm cursor-pointer flex items-center',
                        isEditOptionSelected(option.value, criteria.value)
                          ? 'bg-blue-100 text-blue-800'
                          : 'text-gray-700 hover:bg-gray-100'
                      ]"
                    >
                      <span class="mr-2">
                        <i
                          v-if="isEditOptionSelected(option.value, criteria.value)"
                          class="fas fa-check text-blue-600"
                        ></i>
                        <i
                          v-else
                          class="fas fa-square text-gray-400"
                        ></i>
                      </span>
                      {{ option.label }}
                    </div>
                  </div>
                </div>
              </template>
              <template v-else-if="getKeyType(criteria.key) === 'date'">
                <div class="relative">
                  <el-date-picker
                    v-model="searchCriteria[index].value"
                    type="datetime"
                    :placeholder="`编辑${getSearchKeyLabel(criteria.key)}...`"
                    format="YYYY-MM-DD HH:mm"
                    value-format="YYYY-MM-DD HH:mm"
                    @change="cancelEdit"
                    class="inline-edit-input"
                    ref="editInput"
                  />
                </div>
              </template>
              <template v-else-if="getKeyType(criteria.key) === 'daterange'">
                <div class="relative">
                  <el-date-picker
                    v-model="dateRangeEditValue"
                    type="datetimerange"
                    :placeholder="`编辑${getSearchKeyLabel(criteria.key)}...`"
                    format="YYYY-MM-DD HH:mm"
                    value-format="YYYY-MM-DD HH:mm"
                    :shortcuts="[]"
                    @confirm="handleDateRangeEditConfirm(index)"
                    @blur="handleDateRangeEditBlur(index)"
                    @keyup.escape="cancelEdit"
                    class="inline-edit-input"
                    ref="editInput"
                  />
                </div>
              </template>
            </template>
          </div>

          <!-- 根据键类型动态输入 -->
          <input
            v-if="currentSearchKey && getCurrentKeyType() === 'text'"
            ref="searchInput"
            type="text"
            v-model="searchInputValue"
            :placeholder="`输入${getSearchKeyLabel(currentSearchKey)}...`"
            class="inline-tag-input"
            @keyup.enter="addSearchCriteria"
            @blur="handleTextInputBlur"
            @focus="hideSearchDropdown"
          />

          <!-- 日期输入 -->
          <div v-else-if="currentSearchKey && getCurrentKeyType() === 'date'" class="relative">
            <el-date-picker
              ref="dateInput"
              v-model="selectedDate"
              type="datetime"
              :placeholder="`选择${getSearchKeyLabel(currentSearchKey)}...`"
              format="YYYY-MM-DD HH:mm"
              value-format="YYYY-MM-DD HH:mm"
              @change="handleDateChange"
              class="w-full"
            />
          </div>

          <!-- 日期范围输入 -->
          <div v-else-if="currentSearchKey && getCurrentKeyType() === 'daterange'" class="relative">
            <el-date-picker
              ref="dateRangeInput"
              v-model="selectedDateRange"
              type="datetimerange"
              :placeholder="`选择${getSearchKeyLabel(currentSearchKey)}...`"
              format="YYYY-MM-DD HH:mm"
              value-format="YYYY-MM-DD HH:mm"
              @change="handleDateRangeChange"
              @blur="handleDateRangeBlur"
              class="w-full"
            />
          </div>

          <!-- 其他非文本类型的值下拉菜单 -->
          <div v-else-if="currentSearchKey && getCurrentKeyType() !== 'text'" class="relative">
            <input
              ref="valueInput"
              type="text"
              :value="getCurrentDisplayValue()"
              :placeholder="`选择${getSearchKeyLabel(currentSearchKey)}...`"
              class="inline-tag-input"
              @focus="showValueDropdown = true"
              @keydown.enter="handleValueInputEnter"
              @blur="handleValueInputBlur"
              readonly
            />
            <div
              v-show="showValueDropdown"
              class="absolute z-50 top-full left-0 w-full bg-white shadow-lg rounded-md border border-gray-200 mt-1 max-h-60 overflow-y-auto"
              @click.stop
            >
              <div
                v-for="option in valueDropdownOptions"
                :key="option.value"
                @click="handleOptionClick(option.value)"
                :class="[
                  'px-4 py-2 text-sm cursor-pointer flex items-center',
                  isOptionSelected(option.value)
                    ? 'bg-blue-100 text-blue-800'
                    : 'text-gray-700 hover:bg-gray-100'
                ]"
              >
                <span v-if="getCurrentKeyType() === 'multiselect'" class="mr-2">
                  <i
                    v-if="isOptionSelected(option.value)"
                    class="fas fa-check text-blue-600"
                  ></i>
                  <i
                    v-else
                    class="fas fa-square text-gray-400"
                  ></i>
                </span>
                {{ option.label }}
              </div>
            </div>
          </div>

          <!-- 未选择键时的占位符 -->
          <input
            v-else
            type="text"
            :placeholder="placeholder"
            class="inline-tag-input"
            @focus="handleSearchInputFocus"
            readonly
            ref="searchInput"
          />

          <!-- 清除当前选择的按钮（选择键后可见） -->
          <button
            v-if="currentSearchKey"
            @click="clearCurrentKey"
            class="ml-2 text-gray-400 hover:text-gray-600 transition-colors"
            type="button"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>

      <button
        v-if="showAddButton"
        @click="addSearchCriteria"
        :disabled="!currentSearchKey || (getCurrentKeyType() === 'text' ? !searchInputValue.trim() : getCurrentKeyType() === 'select' ? !selectedValue : getCurrentKeyType() === 'multiselect' ? selectedValues.length === 0 : true)"
        class="ml-2 bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded text-sm disabled:bg-gray-300 disabled:cursor-not-allowed"
      >
        <i class="fas fa-plus mr-1"></i>
        添加
      </button>
    </div>

    <button
      @click="$emit('search', searchCriteria)"
      class="bg-gray-300 hover:bg-gray-400 text-gray-700 px-3 py-2 rounded text-sm"
    >
      <i class="fas fa-search mr-1"></i>
      搜索
    </button>

    <button
      @click="clearSearch"
      class="bg-gray-300 hover:bg-gray-400 text-gray-700 px-3 py-2 rounded text-sm"
    >
      <i class="fas fa-times mr-1"></i>
      清除
    </button>
  </div>
</template>

<script>
export default {
  name: 'SearchFilter',
  props: {
    searchKeys: {
      type: Array,
      default: () => [
        { value: 'unionid', label: '用户UnionID', type: 'text' },
        { value: 'mobile', label: '手机号', type: 'text' },
        { value: 'activityName', label: '活动名称', type: 'text' },
        { value: 'content', label: '投诉内容', type: 'text' },
        { value: 'tid', label: '活动ID', type: 'text' },
        { value: 'status', label: '投诉状态', type: 'multiselect' },
        { value: 'type', label: '投诉类型', type: 'select' },
        { value: 'createTime', label: '创建时间', type: 'date' },
        { value: 'updateTime', label: '更新时间', type: 'date' },
        { value: 'timeRange', label: '时间范围', type: 'daterange' }
      ]
    },
    placeholder: {
      type: String,
      default: '请先选择搜索项'
    },
    showAddButton: {
      type: Boolean,
      default: true
    },
    valueDropdownOptionsMap: {
      type: Object,
      default: () => ({
        'status': [
          { value: 'pending', label: '待处理' },
          { value: 'resolved', label: '已处理' },
          { value: 'dismissed', label: '已驳回' }
        ],
        'type': [
          { value: '1', label: '包含大额奖项' },
          { value: '2', label: '虚假奖项' },
          { value: '3', label: '诱导分享' },
          { value: '4', label: '欺诈行为' },
          { value: '5', label: '发起人违规' },
          { value: '0', label: '其他原因' }
        ]
      })
    },
    searchKeyLabelMap: {
      type: Object,
      default: () => ({
        'unionid': '用户UnionID',
        'mobile': '手机号',
        'activityName': '活动名称',
        'content': '投诉内容',
        'tid': '活动ID',
        'status': '投诉状态',
        'type': '投诉类型'
      })
    }
  },
  emits: ['search', 'clear'],
  data() {
    return {
      searchCriteria: [],
      currentSearchKey: null,
      searchInputValue: '',
      showSearchDropdown: false,
      showValueDropdown: false,
      valueDropdownOptions: [],
      selectedValue: '',
      selectedValues: [],
      selectedDate: null,
      selectedDateRange: [],
      editingIndex: -1,
      dateRangeEditValue: [],
      defaultDateRange: []
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  watch: {
    showSearchDropdown(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.positionSearchDropdown();
        });
      }
    },
    selectedDateRange(newRange) {
      // Watch for date range changes to handle auto-confirmation of defaults
      if (newRange && newRange.length === 2 && this.defaultDateRange.length === 2) {
        const isSameAsDefault = newRange.every((val, idx) => val === this.defaultDateRange[idx]);
        console.log('Date range changed, same as default:', isSameAsDefault);

        // If user didn't change the default range, we may need to auto-confirm
        // This handles the case where datepicker closes without user interaction
        if (isSameAsDefault) {
          // Use setTimeout to ensure this runs after any datepicker UI updates
          setTimeout(() => {
            this.autoConfirmDefaultDateRange();
          }, 100);
        }
      }
    }
  },
  methods: {
    autoConfirmDefaultDateRange() {
      // Auto-confirm the default date range selection
      if (this.selectedDateRange && this.selectedDateRange.length === 2 &&
          this.defaultDateRange && this.defaultDateRange.length === 2) {
        console.log('Auto-confirming default date range:', this.selectedDateRange);
        this.handleDateRangeChange(this.selectedDateRange);
        this.defaultDateRange = [];
      }
    },

    getDefaultDateRange() {
      // 获取今天 00:00:00
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // 获取明天 00:00:00
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      // 格式化为 YYYY-MM-DD HH:mm
      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}`;
      };

      return [formatDate(today), formatDate(tomorrow)];
    },

    selectSearchKey(key) {
      this.currentSearchKey = key;
      this.showSearchDropdown = false;
      this.searchInputValue = '';
      this.selectedValue = '';

      this.populateValueDropdownOptions(key);

      // 如果是日期范围类型，设置默认日期范围
      if (this.getKeyType(key) === 'daterange') {
        this.selectedDateRange = this.getDefaultDateRange();
        // 保存默认范围以便后续自动确认
        this.defaultDateRange = [...this.selectedDateRange];
      }

      this.$nextTick(() => {
        if (this.getCurrentKeyType() === 'text') {
          this.$refs.searchInput?.focus();
        } else if (this.getCurrentKeyType() === 'date') {
          // 日期类型，聚焦日期输入框
          this.$refs.dateInput?.focus();
        } else if (this.getCurrentKeyType() === 'daterange') {
          // 日期范围类型，聚焦并打开日期选择器
          const dateRangeInput = this.$refs.dateRangeInput;
          if (dateRangeInput) {
            dateRangeInput.focus();
            // 编程方式打开日期选择器
            if (dateRangeInput.$el && dateRangeInput.$el.querySelector) {
              const inputElement = dateRangeInput.$el.querySelector('input');
              if (inputElement) {
                inputElement.click();
              }
            }
          }
        } else if (this.getCurrentKeyType() !== 'text') {
          this.showValueDropdown = true;
          this.$refs.valueInput?.focus();
        }
      });
    },

    getCurrentKeyType() {
      if (!this.currentSearchKey) return 'text';
      const key = this.searchKeys.find(k => k.value === this.currentSearchKey);
      return key?.type || 'text';
    },

    getKeyType(key) {
      const keyObj = this.searchKeys.find(k => k.value === key);
      return keyObj?.type || 'text';
    },

    selectValue(value) {
      this.selectedValue = value;
      this.showValueDropdown = false;

      if (this.currentSearchKey && this.selectedValue) {
        this.searchCriteria.push({
          key: this.currentSearchKey,
          value: this.selectedValue
        });
        this.currentSearchKey = null;
        this.selectedValue = '';
      }
    },

    selectEditValue(value, index) {
      this.searchCriteria[index].value = value;
      // 在输入框中显示标签，而不是原始值
      const option = this.valueDropdownOptions.find(opt => opt.value === value);
      this.selectedValue = option ? option.label : value;
      this.showValueDropdown = false;
      this.cancelEdit();
    },

    populateValueDropdownOptions(key) {
      this.valueDropdownOptions = this.valueDropdownOptionsMap[key] || [];
    },

    getSearchKeyLabel(key) {
      return this.searchKeyLabelMap[key] || key;
    },

    getDisplayValue(key, value) {
      if (!value) return '';

      // 日期范围字段（编辑时存储为管道分隔值或数组）
      if (this.getKeyType(key) === 'daterange') {
        if (Array.isArray(value) && value.length === 2) {
          // 编辑时，value 是数组
          return value.join(' ~ ');
        } else if (typeof value === 'string' && value.includes('|')) {
          // 编辑后，value 是管道分隔的字符串
          const dates = value.split('|');
          return dates.join(' ~ ');
        } else {
          // 无效或空的日期范围
          return '';
        }
      }

      // 多选字段，处理管道分隔的值
      if (this.getKeyType(key) === 'multiselect' && value.includes('|')) {
        const values = value.split('|');
        const labels = values.map(val => {
          const option = this.valueDropdownOptionsMap[key]?.find(opt => opt.value === val);
          return option ? option.label : val;
        });
        return labels.join(' | ');
      }

      // 单选字段，查找值的标签
      if (this.valueDropdownOptionsMap[key]) {
        const option = this.valueDropdownOptionsMap[key].find(opt => opt.value === value);
        return option ? option.label : value;
      }

      // 文本字段，直接返回值
      return value;
    },

    getEditDisplayValue(value) {
      if (!value) return '';

      // 单选值，尝试在当前下拉选项中查找标签
      if (!value.includes('|') && this.valueDropdownOptions.length > 0) {
        const option = this.valueDropdownOptions.find(opt => opt.value === value);
        return option ? option.label : value;
      }

      // 多选值
      if (value.includes('|')) {
        const values = value.split('|');
        const labels = values.map(val => {
          const option = this.valueDropdownOptions.find(opt => opt.value === val);
          return option ? option.label : val;
        });
        return labels.join(' | ');
      }

      return value;
    },

    addSearchCriteria() {
      if (this.currentSearchKey) {
        if (this.getCurrentKeyType() === 'text' && this.searchInputValue.trim()) {
          this.searchCriteria.push({
            key: this.currentSearchKey,
            value: this.searchInputValue.trim()
          });
          this.searchInputValue = '';
          this.currentSearchKey = null;
          // 自动触发搜索
          this.$emit('search', this.searchCriteria);
        } else if (this.getCurrentKeyType() === 'select' && this.selectedValue) {
          this.searchCriteria.push({
            key: this.currentSearchKey,
            value: this.selectedValue
          });
          this.selectedValue = '';
          this.currentSearchKey = null;
          this.showValueDropdown = false;
          // 自动触发搜索
          this.$emit('search', this.searchCriteria);
        } else if (this.getCurrentKeyType() === 'multiselect' && this.selectedValues.length > 0) {
          this.searchCriteria.push({
            key: this.currentSearchKey,
            value: this.selectedValues.join('|')
          });
          this.selectedValues = [];
          this.currentSearchKey = null;
          this.showValueDropdown = false;
          // 自动触发搜索
          this.$emit('search', this.searchCriteria);
        }
      }
    },

    removeSearchCriteria(index) {
      this.searchCriteria.splice(index, 1);
      this.cancelEdit();
      // 自动触发搜索
      this.$emit('search', this.searchCriteria);
    },

    editSearchCriteria(index) {
      this.editingIndex = index;
      const criteria = this.searchCriteria[index];

      if (this.getKeyType(criteria.key) === 'select') {
        this.populateValueDropdownOptions(criteria.key);
        this.selectedValue = criteria.value;
      } else if (this.getKeyType(criteria.key) === 'multiselect') {
        this.populateValueDropdownOptions(criteria.key);
        // 多选字段，从 criteria.value 填充 selectedValues
        this.searchInputValue = '';
        this.selectedValue = '';
        if (criteria.value && criteria.value.includes('|')) {
          this.selectedValues = criteria.value.split('|');
        } else if (criteria.value) {
          this.selectedValues = [criteria.value];
        } else {
          this.selectedValues = [];
        }
      } else if (this.getKeyType(criteria.key) === 'date') {
        // 日期字段无需特殊处理，日期选择器会自动显示当前值
        this.searchInputValue = '';
        this.selectedValue = '';
      } else if (this.getKeyType(criteria.key) === 'daterange') {
        // 日期范围字段，将管道分隔的字符串转换为数组供日期选择器使用
        this.searchInputValue = '';
        this.selectedValue = '';
        if (criteria.value && criteria.value.includes('|')) {
          this.dateRangeEditValue = criteria.value.split('|');
        } else {
          // 设置默认范围：今天 00:00 到明天 00:00
          this.dateRangeEditValue = this.getDefaultDateRange();
        }
      } else {
        // 文本字段，直接显示值
        this.searchInputValue = criteria.value;
      }

      this.$nextTick(() => {
        if (this.getKeyType(criteria.key) === 'text') {
          this.$refs.editInput?.[index]?.focus();
        } else if (this.getKeyType(criteria.key) === 'select' || this.getKeyType(criteria.key) === 'multiselect') {
          this.showValueDropdown = true;
          this.$refs.editInput?.[index]?.focus();
        } else if (this.getKeyType(criteria.key) === 'date' || this.getKeyType(criteria.key) === 'daterange') {
          // 聚焦输入框，Element Plus 日期选择器会自动显示选择器界面
          this.$refs.editInput?.[index]?.focus();
        }
      });
    },

    updateSearchCriteria(index) {
      const criteria = this.searchCriteria[index];
      if (this.getKeyType(criteria.key) === 'text' && this.searchInputValue.trim()) {
        criteria.value = this.searchInputValue.trim();
        this.cancelEdit();
        // 自动触发搜索
        this.$emit('search', this.searchCriteria);
      }
    },

    cancelEdit() {
      // 值已在各自的处理方法中保存（如 toggleEditMultiSelectValue、selectEditValue）
      // 这里只清理编辑状态
      this.editingIndex = -1;
      this.searchInputValue = '';
      this.dateRangeEditValue = [];
      this.showValueDropdown = false;
    },

    clearSearch() {
      this.searchCriteria = [];
      this.searchInputValue = '';
      this.currentSearchKey = null;
      this.$emit('clear');
    },

    clearCurrentKey() {
      this.currentSearchKey = null;
      this.searchInputValue = '';
      this.selectedValue = '';
      this.showValueDropdown = false;
    },

    handleClickOutside(event) {
      // 关闭搜索下拉菜单
      if (this.$refs.searchDropdown &&
          !this.$refs.searchDropdown.contains(event.target) &&
          !this.$refs.searchInput?.contains(event.target)) {
        this.showSearchDropdown = false;
      }

      // 退出编辑状态
      if (this.editingIndex !== -1 && this.$el) {
        const editingTag = this.$el.querySelector('.inline-tag.editing');
        const isInsideDatePicker = this.isClickInsideDatePicker(event.target);

        if (editingTag && !editingTag.contains(event.target) && !isInsideDatePicker) {
          // 多选/单选的值已在各自的 toggle/select 方法中直接保存到 searchCriteria
          this.cancelEdit();
        }
      }

      // 关闭值下拉菜单
      if (this.showValueDropdown) {
        const valueDropdowns = this.$el.querySelectorAll('.absolute.z-50');
        let isInsideValueDropdown = false;

        valueDropdowns.forEach(dropdown => {
          if (dropdown.contains(event.target)) {
            isInsideValueDropdown = true;
          }
        });

        if (!isInsideValueDropdown) {
          // 多选场景：有选中值时自动确认
          if (this.getCurrentKeyType() === 'multiselect' && this.selectedValues.length > 0) {
            this.confirmMultiSelect();
          } else {
            this.showValueDropdown = false;
          }
        }
      }
    },

    handleSearchInputFocus() {
      this.showSearchDropdown = true;
      this.$nextTick(() => {
        this.positionSearchDropdown();
      });
    },

    positionSearchDropdown() {
      if (this.$refs.searchInput && this.$refs.searchDropdown) {
        const input = this.$refs.searchInput;
        const dropdown = this.$refs.searchDropdown;

        // 获取输入框相对于容器的位置
        const inputRect = input.getBoundingClientRect();
        const containerRect = input.parentElement.getBoundingClientRect();

        // 计算相对于容器的左边位置
        const leftPosition = inputRect.left - containerRect.left;

        // 将下拉菜单定位在输入框正下方
        dropdown.style.position = 'absolute';
        dropdown.style.top = '100%';
        dropdown.style.left = leftPosition + 'px';
        dropdown.style.marginTop = '2px';
      }
    },

    getCurrentDisplayValue() {
      if (this.getCurrentKeyType() === 'multiselect') {
        if (this.selectedValues.length === 0) return '';
        const labels = this.selectedValues.map(value => {
          const option = this.valueDropdownOptions.find(opt => opt.value === value);
          return option ? option.label : value;
        });
        return labels.join(' | ');
      }
      return this.selectedValue;
    },

    handleOptionClick(value) {
      if (this.getCurrentKeyType() === 'multiselect') {
        this.toggleMultiSelectValue(value);
      } else {
        this.selectValue(value);
      }
    },

    toggleMultiSelectValue(value) {
      const index = this.selectedValues.indexOf(value);
      if (index === -1) {
        this.selectedValues.push(value);
      } else {
        this.selectedValues.splice(index, 1);
      }
    },

    isEditOptionSelected(optionValue, currentValue) {
      if (!currentValue) return false;
      if (currentValue.includes('|')) {
        return currentValue.split('|').includes(optionValue);
      }
      return currentValue === optionValue;
    },

    toggleEditMultiSelectValue(value, index) {
      const currentValue = this.searchCriteria[index].value;
      let values = [];

      if (currentValue && currentValue.includes('|')) {
        values = currentValue.split('|');
      } else if (currentValue) {
        values = [currentValue];
      }

      const valueIndex = values.indexOf(value);
      if (valueIndex === -1) {
        values.push(value);
      } else {
        values.splice(valueIndex, 1);
      }

      const newValue = values.join('|');
      this.searchCriteria[index].value = newValue;
      // 同步 selectedValues 保持一致性
      this.selectedValues = values;
    },

    isOptionSelected(value) {
      if (this.getCurrentKeyType() === 'multiselect') {
        return this.selectedValues.includes(value);
      }
      return this.selectedValue === value;
    },

    handleValueInputEnter() {
      if (this.getCurrentKeyType() === 'multiselect' && this.selectedValues.length > 0) {
        this.confirmMultiSelect();
      } else if (this.getCurrentKeyType() === 'select' && this.selectedValue) {
        this.addSearchCriteria();
      }
    },

    handleDateChange(date) {
      if (date) {
        this.searchCriteria.push({
          key: this.currentSearchKey,
          value: date
        });
        this.currentSearchKey = null;
        this.selectedDate = null;
        // 自动触发搜索
        this.$emit('search', this.searchCriteria);
      }
    },

    handleDateRangeChange(dateRange) {
      if (dateRange && dateRange.length === 2) {
        const rangeValue = dateRange.join('|');
        this.searchCriteria.push({
          key: this.currentSearchKey,
          value: rangeValue
        });
        this.currentSearchKey = null;
        this.selectedDateRange = [];
        // 强制更新 UI 显示新标签
        this.$forceUpdate();
        // 自动触发搜索
        this.$emit('search', this.searchCriteria);
      }
    },

    handleDateRangeBlur() {
      // 处理主日期范围输入框的失去焦点事件 - 如果选择完整则确认
      if (this.selectedDateRange && this.selectedDateRange.length === 2) {
        // 始终在失去焦点时确认，即使是默认值（用户未更改选择）
        this.handleDateRangeChange(this.selectedDateRange);
        // 重置默认范围追踪
        this.defaultDateRange = [];
      }
    },

    handleDateRangeEditConfirm(index) {
      if (this.dateRangeEditValue && this.dateRangeEditValue.length === 2) {
        this.searchCriteria[index].value = this.dateRangeEditValue.join('|');
        this.cancelEdit();
        // 自动触发搜索
        this.$emit('search', this.searchCriteria);
      }
    },

    handleDateRangeEditBlur(index) {
      // 日期范围：失去焦点时只有选择了完整范围才确认
      if (this.dateRangeEditValue && this.dateRangeEditValue.length === 2) {
        this.searchCriteria[index].value = this.dateRangeEditValue.join('|');
        this.cancelEdit();
        // 自动触发搜索
        this.$emit('search', this.searchCriteria);
      }
    },

    isClickInsideDatePicker(targetElement) {
      if (!targetElement) return false;

      // 检查点击是否在 Element Plus 日期选择器内
      // Element Plus 日期选择器元素有特定的 class 模式
      let element = targetElement;
      while (element && element !== document.body) {
        // 检查日期选择器面板（主下拉框）
        if (element.classList?.contains('el-picker-panel') ||
            element.classList?.contains('el-date-picker') ||
            element.classList?.contains('el-date-range-picker') ||
            (element.className && typeof element.className === 'string' &&
             (element.className.includes('el-picker-panel') ||
              element.className.includes('el-date-picker') ||
              element.className.includes('el-date-range-picker')))) {
          return true;
        }

        // 检查日期选择器输入包装器
        if (element.classList?.contains('el-date-editor') ||
            (element.className && typeof element.className === 'string' &&
             element.className.includes('el-date-editor'))) {
          return true;
        }

        element = element.parentElement;
      }

      return false;
    },

    isClickInsideDropdown(event) {
      if (!event || !this.$refs.valueInput) return false;

      // 检查相关目标（接收焦点的元素）是否在下拉菜单内
      const dropdown = this.$el.querySelector('.absolute.z-50');
      const relatedTarget = event.relatedTarget;

      if (dropdown && relatedTarget) {
        return dropdown.contains(relatedTarget);
      }

      return false;
    },

    handleValueInputBlur() {
      // 多选时自动确认
      if (this.getCurrentKeyType() === 'multiselect' && this.selectedValues.length > 0) {
        this.confirmMultiSelect();
      }
    },

    handleTextInputBlur() {
      // 文本输入：失去焦点时如果有输入值则添加条件
      if (this.getCurrentKeyType() === 'text' && this.searchInputValue.trim()) {
        this.addSearchCriteria();
      }
    },

    confirmMultiSelect() {
      if (this.selectedValues.length > 0) {
        this.searchCriteria.push({
          key: this.currentSearchKey,
          value: this.selectedValues.join('|')
        });
        this.currentSearchKey = null;
        this.selectedValues = [];
        this.showValueDropdown = false;
        // 自动触发搜索
        this.$emit('search', this.searchCriteria);
      }
    },

    hideSearchDropdown() {
      this.showSearchDropdown = false;
    }
  }
}
</script>

<style scoped>
.inline-tags-container {
  height: 40px;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem 0.375rem;
  padding: 0 0.75rem;
  background: white;
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 0.5rem;
  min-width: 256px;
  box-sizing: border-box;
}

.inline-tags-container:focus-within {
  outline: 2px solid #3b82f6;
  outline-offset: -1px;
}

.inline-tag {
  display: inline-flex;
  align-items: center;
  background-color: #dbeafe;
  color: #1e40af;
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  line-height: 1;
}

.inline-tag button {
  margin-left: 0.25rem;
  color: #3b82f6;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
}

.inline-tag button:hover {
  color: #1e40af;
  background-color: #bfdbfe;
}

.inline-tag-input {
  flex: 1;
  min-width: 120px;
  background: transparent;
  border: none;
  outline: none;
  font-size: 0.875rem;
  padding: 0;
  height: 38px;
  line-height: 38px;
}

.inline-edit-input {
  background: white;
  border: 1px solid #3b82f6;
  border-radius: 0.25rem;
  outline: none;
  font-size: 0.875rem;
  padding: 0.25rem 0.5rem;
  min-width: 100px;
}

/* Style for date picker in edit mode */
.inline-edit-input .el-input__inner {
  height: 28px !important;
  line-height: 28px !important;
  font-size: 0.875rem !important;
  padding: 0 0.5rem !important;
}

.inline-edit-input .el-input__prefix,
.inline-edit-input .el-input__suffix {
  display: none !important;
}

.inline-tag.editing {
  background-color: #eff6ff;
  border-color: #3b82f6;
}
</style>
